import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Leaf, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { API } from "./site-data";

const EMPTY = { name: "", email: "", phone: "", location: "", message: "" };

export const LeadForm = ({ open, setOpen, interest, setInterest }) => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, interest });
      toast.success(
        interest === "quote"
          ? "Quote request sent — we'll be in touch soon."
          : "You're on the list. Welcome to the garden."
      );
      setForm(EMPTY);
      setOpen(false);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-lg bg-sg-bg border border-sg-border px-4 py-3 text-sm text-sg-fg placeholder:text-sg-muted/60 focus:outline-none focus:border-sg-primary transition-colors duration-300";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="sm:max-w-[460px] bg-sg-card border-sg-border text-sg-fg p-0 overflow-hidden"
        data-testid="lead-dialog"
      >
        <div className="p-6 sm:p-8">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-2">
              <span className="grid place-items-center w-7 h-7 rounded-full bg-sg-primary text-sg-bg">
                <Leaf size={14} />
              </span>
              <span className="font-display text-base text-sg-fg">MySolarGarden</span>
            </div>
            <DialogTitle className="font-display text-2xl tracking-tight text-sg-fg">
              {interest === "quote" ? "Request a quote" : "Join the waitlist"}
            </DialogTitle>
            <DialogDescription className="text-sm text-sg-muted">
              Tell us a little about your space and we'll follow up personally.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-5 flex gap-2 rounded-full bg-sg-bg p-1 border border-sg-border">
            {[
              { k: "quote", label: "Get a Quote" },
              { k: "waitlist", label: "Waitlist" },
            ].map((t) => (
              <button
                key={t.k}
                type="button"
                onClick={() => setInterest(t.k)}
                data-testid={`interest-${t.k}`}
                className={`relative flex-1 rounded-full py-2 text-sm transition-colors duration-300 ${
                  interest === t.k ? "text-sg-bg" : "text-sg-muted hover:text-sg-fg"
                }`}
              >
                {interest === t.k && (
                  <motion.span
                    layoutId="interest-pill"
                    className="absolute inset-0 rounded-full bg-sg-primary"
                    transition={{ type: "spring", stiffness: 400, damping: 32 }}
                  />
                )}
                <span className="relative z-10">{t.label}</span>
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="mt-5 space-y-3">
            <input
              className={inputCls}
              placeholder="Full name"
              value={form.name}
              onChange={set("name")}
              data-testid="lead-name"
            />
            <input
              className={inputCls}
              type="email"
              placeholder="Email address"
              value={form.email}
              onChange={set("email")}
              data-testid="lead-email"
            />
            <div className="grid grid-cols-2 gap-3">
              <input
                className={inputCls}
                placeholder="Phone (optional)"
                value={form.phone}
                onChange={set("phone")}
                data-testid="lead-phone"
              />
              <input
                className={inputCls}
                placeholder="City / ZIP"
                value={form.location}
                onChange={set("location")}
                data-testid="lead-location"
              />
            </div>
            <textarea
              className={`${inputCls} resize-none`}
              rows={3}
              placeholder={
                interest === "quote"
                  ? "Tell us about your patio or balcony..."
                  : "Anything you'd like us to know? (optional)"
              }
              value={form.message}
              onChange={set("message")}
              data-testid="lead-message"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="lead-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-sg-primary px-6 py-3.5 text-sm font-medium text-sg-bg hover:bg-sg-fg transition-colors duration-300 disabled:opacity-60"
            >
              {loading && <Loader2 size={15} className="animate-spin" />}
              {interest === "quote" ? "Send Quote Request" : "Join the Waitlist"}
            </button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};
